import {
  CheckCircleFilled,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FilePdfOutlined,
  PlusOutlined,
  StopOutlined,
  SwapOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import ProjectResourcesDrawer from '../../components/ResourcePlanning/ProjectResourcesDrawer';
import {
  colorOf,
  CompanySelect,
  DATE,
  DISPLAY_DATE,
  IconButton,
  PdfPreview,
  PlanningLink,
  today,
  useFeedback,
  useMe,
  useProjectResponsibles,
  useProjects,
  useSystems,
} from '../../components/ResourcePlanning/shared';
import type { Project } from '../../DTOS/ResourcePlanning';
import api from '../../services/resourcePlanning/ResourcePlanningService';

interface Values {
  number: string;
  companyId: string;
  responsibleUserId?: string;
  systemTypeIds: string[];
  period: [Dayjs, Dayjs];
  isActive: boolean;
}
type Status = 'all' | 'active' | 'inactive';

const ProjectsTab: React.FC = () => {
  const { data = [], isLoading, error: loadError } = useProjects();
  // Team leads read projects and PDFs; only planners create, edit or attach documents.
  const { canPlan } = useMe();
  const { data: systems = [] } = useSystems();
  const { data: responsibles = [] } = useProjectResponsibles();
  const [staffingId, setStaffingId] = useState<string>();
  const feedback = useFeedback();
  const [search, setSearch] = useState('');
  const [systemFilter, setSystemFilter] = useState<string[]>([]);
  const [status, setStatus] = useState<Status>('all');
  const [editing, setEditing] = useState<Partial<Project> | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewing, setPreviewing] = useState<Project | null>(null);
  const [form] = Form.useForm<Values>();
  const mutation = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onError: feedback.error });
  const open = (project?: Project) => {
    form.resetFields();
    setFile(null);
    setEditing(project || {});
    form.setFieldsValue(
      project
        ? ({
            number: project.number,
            companyId: project.companyId || undefined,
            responsibleUserId: project.responsibleUserId || undefined,
            isActive: project.isActive,
            systemTypeIds: project.systems.map((system) => system.id),
            period: [dayjs(project.startDate), dayjs(project.endDate)],
          } as Values)
        : { isActive: true, systemTypeIds: [] },
    );
  };
  const save = async () => {
    try {
      const values = await form.validateFields();
      mutation.mutate(async () => {
        const id = await api.saveProject({
          id: editing?.id,
          number: values.number,
          companyId: values.companyId,
          responsibleUserId: values.responsibleUserId || null,
          systemTypeIds: values.systemTypeIds,
          startDate: values.period[0].format(DATE),
          endDate: values.period[1].format(DATE),
          isActive: values.isActive,
          expectedVersion: editing?.rowVersion || 0,
        });
        setEditing(null);
        feedback.success('Proiect salvat.');
        if (file) {
          try {
            await api.uploadPdf(id, file, editing?.id ? editing.rowVersion! + 1 : 1);
            feedback.success('PDF incarcat.');
          } catch (error) {
            feedback.error(
              new Error(`Proiectul a fost salvat. Incarcarea PDF-ului a esuat: ${(error as Error).message}`),
            );
          }
        }
      });
    } catch (error) {
      feedback.error(error);
    }
  };
  // Inactive systems remain selectable only when already linked to the edited contract.
  const linked = new Set(editing?.systems?.map((system) => system.id) || []);
  const rows = data.filter(
    (project) =>
      `${project.number} ${project.name} ${project.responsibleName || ''} ${project.systems.map((system) => system.name).join(' ')}`
        .toLowerCase()
        .includes(search.toLowerCase()) &&
      (!systemFilter.length || project.systems.some((system) => systemFilter.includes(system.id))) &&
      (status === 'all' || project.isActive === (status === 'active')),
  );
  const elapsed = (project: Project) => {
    const total = dayjs(project.endDate).diff(project.startDate, 'day') + 1,
      done = dayjs(today()).diff(project.startDate, 'day') + 1;
    return Math.max(0, Math.min(100, Math.round((done * 100) / total)));
  };
  return (
    <Space direction='vertical' style={{ width: '100%' }} size='middle'>
      <Space wrap>
        <Input.Search
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder='Numar, client sau sistem'
          allowClear
          style={{ width: 260 }}
        />
        <Select
          mode='multiple'
          allowClear
          maxTagCount='responsive'
          showSearch
          optionFilterProp='label'
          value={systemFilter}
          onChange={setSystemFilter}
          placeholder='Sisteme'
          style={{ width: 220 }}
          options={systems.map((system) => ({ value: system.id, label: system.name }))}
        />
        <Select<Status>
          value={status}
          onChange={setStatus}
          style={{ width: 160 }}
          options={[
            { value: 'all', label: 'Toate' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
        {canPlan && <IconButton title='Proiect nou' type='primary' icon={<PlusOutlined />} onClick={() => open()} />}
      </Space>
      {loadError && <Typography.Text type='danger'>{(loadError as Error).message}</Typography.Text>}
      <Table<Project>
        rowKey='id'
        loading={isLoading}
        dataSource={rows}
        scroll={{ x: 1000 }}
        size='middle'
        columns={[
          {
            title: 'Contract',
            dataIndex: 'number',
            sorter: (a, b) => a.number.localeCompare(b.number),
            render: (_, project) => (
              <span>
                <span
                  style={{
                    display: 'inline-block',
                    width: 4,
                    height: 16,
                    borderRadius: 2,
                    background: colorOf(project.id),
                    marginRight: 8,
                    verticalAlign: 'middle',
                  }}
                />
                {project.number}
              </span>
            ),
          },
          {
            title: 'Client',
            dataIndex: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (_, project) =>
              project.companyId ? (
                project.name
              ) : (
                <Tooltip title='Proiect salvat inainte de selectarea clientilor. Editati-l pentru a alege clientul.'>
                  <Space size={4}>
                    {project.name}
                    <Tag color='orange'>Fara client</Tag>
                  </Space>
                </Tooltip>
              ),
          },
          {
            title: 'Responsabil',
            render: (_, project) => project.responsibleName || <Tag color='orange'>Nedesemnat</Tag>,
          },
          {
            title: 'Sisteme',
            render: (_, project) => (
              <Space size={[0, 4]} wrap>
                {project.systems.map((system) => (
                  <Tag key={system.id} color={system.isActive ? 'blue' : 'default'}>
                    {system.name}
                  </Tag>
                ))}
              </Space>
            ),
          },
          {
            title: 'Perioada',
            width: 230,
            sorter: (a, b) => a.startDate.localeCompare(b.startDate),
            render: (_, project) => (
              <Tooltip title={`${elapsed(project)}% din perioada contractului a trecut`}>
                <div style={{ fontSize: 12 }}>
                  {dayjs(project.startDate).format(DISPLAY_DATE)} – {dayjs(project.endDate).format(DISPLAY_DATE)}
                </div>
                <Progress percent={elapsed(project)} showInfo={false} size='small' strokeColor={colorOf(project.id)} />
              </Tooltip>
            ),
          },
          {
            title: 'Stare',
            width: 70,
            align: 'center',
            render: (_, project) =>
              project.isActive ? (
                <Tooltip title='Activ'>
                  <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                </Tooltip>
              ) : (
                <Tooltip title='Inactiv'>
                  <StopOutlined style={{ color: '#bfbfbf', fontSize: 16 }} />
                </Tooltip>
              ),
          },
          {
            title: 'PDF',
            width: 190,
            render: (_, project) => (
              <Space size={4}>
                {project.hasPdf && (
                  <IconButton
                    title={`Previzualizeaza ${project.pdfName}`}
                    size='small'
                    icon={<EyeOutlined />}
                    onClick={() => setPreviewing(project)}
                  />
                )}
                {project.hasPdf && (
                  <IconButton
                    title='Descarca'
                    size='small'
                    icon={<DownloadOutlined />}
                    onClick={() => mutation.mutate(() => api.downloadPdf(project))}
                  />
                )}
                {canPlan && (
                  <Upload
                    accept='.pdf,application/pdf'
                    showUploadList={false}
                    beforeUpload={(pdf) => {
                      mutation.mutate(async () => {
                        await api.uploadPdf(project.id, pdf, project.rowVersion);
                        feedback.success('PDF incarcat.');
                      });
                      return false;
                    }}
                  >
                    <IconButton
                      title={project.hasPdf ? 'Inlocuieste PDF' : 'Incarca PDF'}
                      size='small'
                      icon={project.hasPdf ? <SwapOutlined /> : <UploadOutlined />}
                      loading={mutation.isPending}
                    />
                  </Upload>
                )}
                {canPlan && project.hasPdf && (
                  <Popconfirm
                    title='Eliminati PDF-ul?'
                    onConfirm={() =>
                      mutation.mutate(async () => {
                        await api.removePdf(project.id, project.rowVersion);
                        feedback.success('PDF eliminat.');
                      })
                    }
                  >
                    <IconButton title='Elimina PDF' size='small' danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Space>
            ),
          },
          {
            title: '',
            width: 270,
            fixed: 'right',
            render: (_, project) => (
              <Space>
                <Button size='small' onClick={() => setStaffingId(project.id)}>
                  Resurse
                </Button>
                <PlanningLink projectId={project.id} date={project.startDate} name={project.number} />
                {canPlan && (
                  <IconButton title='Editeaza' size='small' icon={<EditOutlined />} onClick={() => open(project)} />
                )}
              </Space>
            ),
          },
        ]}
      />
      <Modal
        open={editing !== null}
        title={editing?.id ? 'Editeaza proiectul' : 'Proiect nou'}
        onCancel={() => setEditing(null)}
        onOk={save}
        confirmLoading={mutation.isPending}
        okText='Salveaza'
        cancelText='Renunta'
        destroyOnHidden
      >
        <Form form={form} layout='vertical'>
          <Form.Item name='number' label='Numar contract' rules={[{ required: true, whitespace: true }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item
            name='companyId'
            label='Client'
            rules={[{ required: true, message: 'Selectati clientul.' }]}
            extra={
              editing?.id && !editing.companyId
                ? `Denumire anterioara: ${editing.name}. Selectati clientul din lista de companii.`
                : undefined
            }
          >
            <CompanySelect selectedLabel={editing?.companyId ? editing.name : undefined} />
          </Form.Item>
          <Form.Item
            name='responsibleUserId'
            label='Responsabil proiect'
            extra='Sef de echipa, director tehnic sau responsabil de planificare.'
          >
            <Select
              showSearch
              allowClear
              optionFilterProp='label'
              placeholder='Selectati responsabilul'
              options={[
                ...responsibles.map((person) => ({ value: person.id, label: person.name })),
                ...(editing?.responsibleUserId &&
                !responsibles.some((person) => person.id === editing.responsibleUserId)
                  ? [{ value: editing.responsibleUserId, label: `${editing.responsibleName} (desemnare existenta)` }]
                  : []),
              ]}
            />
          </Form.Item>
          <Form.Item
            name='systemTypeIds'
            label='Sisteme'
            rules={[{ required: true, type: 'array', min: 1, message: 'Selectati cel putin un sistem.' }]}
          >
            <Select
              mode='multiple'
              showSearch
              optionFilterProp='label'
              placeholder='Selectati unul sau mai multe sisteme'
              options={systems
                .filter((system) => system.isActive || linked.has(system.id))
                .map((system) => ({
                  value: system.id,
                  label: system.isActive ? system.name : `${system.name} (inactiv)`,
                }))}
            />
          </Form.Item>
          <Form.Item name='period' label='Data inceput – data final' rules={[{ required: true }]}>
            <DatePicker.RangePicker format={DISPLAY_DATE} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name='isActive' label='Proiect activ' valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item label='Contract PDF (optional, maxim 20 MB)'>
            <Space align='start'>
              <Upload
                accept='.pdf,application/pdf'
                maxCount={1}
                beforeUpload={(pdf) => {
                  setFile(pdf);
                  return false;
                }}
                onRemove={() => setFile(null)}
                fileList={file ? [{ uid: 'pdf', name: file.name, status: 'done' }] : []}
              >
                <IconButton title={editing?.hasPdf ? 'Inlocuieste PDF' : 'Selecteaza PDF'} icon={<UploadOutlined />} />
              </Upload>
              {editing?.hasPdf && !file && (
                <Button type='link' icon={<FilePdfOutlined />} onClick={() => setPreviewing(editing as Project)}>
                  {editing.pdfName}
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <PdfPreview project={previewing} onClose={() => setPreviewing(null)} />
      {staffingId && data.some((project) => project.id === staffingId) && (
        <ProjectResourcesDrawer
          key={staffingId}
          project={data.find((project) => project.id === staffingId)!}
          onClose={() => setStaffingId(undefined)}
        />
      )}
    </Space>
  );
};
export default ProjectsTab;
